import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { CircleNotch } from "@phosphor-icons/react";
import { api } from "@alfred/convex/_generated/api";
import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { relativeTime } from "~/lib/utils";

export const Route = createFileRoute("/agents")({
  component: AgentRunsPage,
});

function AgentRunsPage() {
  const runs = useQuery(api.runs.recent, { limit: 40 });
  return (
    <div className="mx-auto max-w-5xl px-4 py-6 lg:px-6">
      <h1 className="text-xl font-semibold tracking-tight">Agent runs</h1>
      <p className="mb-4 text-xs text-muted-foreground">
        Every Cursor agent invocation Alfred has made, newest first.
      </p>
      {runs === undefined ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-2xl" />
          ))}
        </div>
      ) : runs.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            No runs yet. Pick a ticket on the board and click "Run Alfred".
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {runs.map((r) => (
            <Card key={r._id}>
              <CardHeader className="flex flex-row items-start gap-3 space-y-0 pb-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <CircleNotch
                    size={18}
                    weight="bold"
                    className={
                      r.status === "running" || r.status === "pending"
                        ? "animate-spin"
                        : undefined
                    }
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-sm">
                    <Link
                      to="/tickets/$key"
                      params={{ key: r.jiraKey }}
                      className="text-primary hover:underline"
                    >
                      {r.jiraKey}
                    </Link>
                    <span className="ml-2 text-muted-foreground">
                      {r.repoFullName ?? "—"}
                    </span>
                  </CardTitle>
                  <p className="text-[11px] text-muted-foreground">
                    Run {String(r._id).slice(0, 10)}… • started{" "}
                    {relativeTime(r.startedAt)}
                    {r.finishedAt
                      ? ` • finished ${relativeTime(r.finishedAt)}`
                      : ""}
                  </p>
                </div>
                <Badge
                  variant={
                    r.status === "succeeded"
                      ? "success"
                      : r.status === "failed"
                        ? "danger"
                        : "info"
                  }
                >
                  {r.status.replace(/_/g, " ")}
                </Badge>
              </CardHeader>
              {r.error ? (
                <CardContent>
                  <p className="line-clamp-3 text-xs text-[color:var(--status-error)]">
                    {r.error}
                  </p>
                </CardContent>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
